<script lang="ts">
  import { createEventDispatcher, onDestroy, onMount } from 'svelte';
  import { cn } from '@/lib/utils';
  import { getTypedAm5 } from '@/Util/XertAm5';
  import { parseStorage, saveStorage } from '@/Util/utils';
  import {
    AXES,
    BREAKTHROUGHS_VISIBLE_IN,
    BREAKTHROUGH_COLOR,
    GROUP_SERIES,
    MARKER_KINDS,
    SERIES,
    type HistoryGroup,
  } from './seriesConfig';
  import { RACE_EVENT_COLORS } from '@/Common/activityTagOptions';
  import { buildBreakthroughs, markersToShow, isBreakthrough, buildDays, buildMarkers, chartTimeToDate, mergeMatchedDays, type ActivityMarker, type Breakthrough, type RaceEvent, type ChartDay, type ChartMarker } from './chartData';
  import { addBreakthroughDiamonds, addBreakthroughSeries, addDayTooltip, addEventsAxis, addMarkerLanes, addProgramStartGuide, addSelectionCursor, addTodayGuide, colinearScales, keepTooltipsOnScreen } from './chartHelpers';
  import type { MatchedDay, MeasureRow } from './progressionApi';

  // Needs amCharts loaded first: mount it inside <Am5Loader>.
  const { am5, am5xy, am5themes_Animated } = getTypedAm5();
  am5.addLicense('AM5C87942260');

  export let group: HistoryGroup = 'XPMC';
  /** The daily fitness history, as /my-fitness/measures returns it. */
  export let rows: MeasureRow[] | null = null;
  /** The athlete's ir_params; where present, the threshold power scale is drawn colinear with the load scale. */
  export let irParams: any = null;
  export let breakthroughs: Breakthrough[] | null = null;
  export let raceEvents: RaceEvent[] | null = null;
  /** Flagged / manually set activities and Planner notes; only the notes are drawn here (XPMC). */
  export let markers: ActivityMarker[] | null = null;
  /** The date range to draw, as `YYYY-MM-DD` in the athlete's timezone. `from` is empty for "all". */
  export let from: string = '';
  export let to: string = '';
  export let today: string = '';
  /** Where an XFAI plan starts (or will start), `YYYY-MM-DD` in the athlete's timezone; '' when there is none. */
  export let programStart: string = '';
  export let hideFuture: boolean = false;
  /** While a filter is on: the XSS of the activities that matched, day by day. The rest is drawn in grey. */
  export let matchedDays: MatchedDay[] | null = null;

  const dispatch = createEventDispatcher<{ select: { from: string; to: string } }>();
  /** Dragging across the chart picks out a stretch of days, which the page turns into the date range. */
  const selected = (from: number, to: number) => dispatch('select', { from: chartTimeToDate(from), to: chartTimeToDate(to) });

  let chartEl: HTMLDivElement;
  let mounted = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let root: any = null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let seriesById: Record<string, any> = {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let axisById: Record<string, any> = {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let dateAxisRef: any = null;
  /** Where the middle of each scale is, across the chart, so its name can be centred over it. */
  let axisCentres: Record<string, number> = {};
  /** How wide each name is, and how wide the row they sit in is, to keep them from running into each other. */
  let headerWidths: Record<string, number> = {};
  let headerRowWidth = 0;
  const HEADER_SPACING_PX = 8;

  /**
   * Where the left edge of each name goes: centred over its scale, pushed along where that would put it on
   * top of its neighbour, and kept inside the row at both ends.
   */
  function placeHeaders(centres: Record<string, number>, widths: Record<string, number>, rowWidth: number): Record<string, number> {
    const items = Object.entries(centres)
      .map(([id, centre]) => ({ id, width: widths[id] ?? 0, centre }))
      .sort((a, b) => a.centre - b.centre);
    const left: Record<string, number> = {};
    let edge = -Infinity;
    for (const item of items) {
      left[item.id] = Math.max(0, item.centre - item.width / 2, edge + HEADER_SPACING_PX);
      edge = left[item.id] + item.width;
    }
    let limit = rowWidth > 0 ? rowWidth : Infinity;
    for (const item of [...items].reverse()) {
      left[item.id] = Math.max(0, Math.min(left[item.id], limit - item.width));
      limit = left[item.id] - HEADER_SPACING_PX;
    }
    return left;
  }
  $: headerLeft = placeHeaders(axisCentres, headerWidths, headerRowWidth);

  // Which series the athlete has switched on or off, per group; anything not touched keeps its default.
  // Kept in the session, so the charts open the same way after a reload.
  const savedLines = parseStorage('progressionCharts').lines;
  let visibility: Record<string, boolean> =
    savedLines && typeof savedLines === 'object'
      ? (Object.fromEntries(Object.entries(savedLines).filter(([, value]) => typeof value === 'boolean')) as Record<string, boolean>)
      : {};
  const key = (g: HistoryGroup, id: string) => `${g}:${id}`;
  const defaultOn = (g: HistoryGroup, id: string) =>
    id === 'breakthroughs' ? BREAKTHROUGHS_VISIBLE_IN.includes(g) : id === 'notes' ? g === 'XPMC' : id === 'raceEvents' ? true : !SERIES[id].hidden;
  const isOn = (g: HistoryGroup, id: string, state = visibility) => state[key(g, id)] ?? defaultOn(g, id);

  $: filtered = matchedDays != null;
  // Whether the breakthroughs have their own strip above the chart (XPMC, with any to show).
  $: stripShown = group === 'XPMC' && marks.length > 0;
  // Whether the Planner notes have their strip (above the breakthroughs') on XPMC.
  $: notesShown = group === 'XPMC' && notes.length > 0 && isOn(group, 'notes', visibility);
  /** How far down the chart the scale names (and, without any strip, the chart) start. */
  $: stripsPx = (notesShown ? NOTES_STRIP_PX : 0) + (stripShown ? STRIP_PX : 0);
  /** The series a group draws: the grey "filtered out" bar only exists while a filter is narrowing things. */
  const idsFor = (g: HistoryGroup, withFilteredOut: boolean) => GROUP_SERIES[g].filter((id) => id !== 'xout' || withFilteredOut);
  /** Whether a series shares its scale with others in the group (XSS split, loads, stress), so what is on sets its range. */
  const sharesAxis = (g: HistoryGroup, id: string) => GROUP_SERIES[g].some((other) => other !== id && SERIES[other].axis === SERIES[id].axis);

  // With "Hide future" off, a range that ends today (a preset) or is left open stays open to whatever
  // is scheduled after it. A range with its own past end date (a custom pick, or a drag on the chart)
  // stops exactly there -- picking a specific week shouldn't pull in a year of future activities.
  $: drawTo = !hideFuture && today && (to === today || to === '') ? '' : to;
  $: days = withWeight(buildDays(rows, from, drawTo, today, hideFuture));

  // Only real activity rows carry a weight; the daily rows generated between activities (and on to today) don't.
  // Carry the last recorded weight across them, so Weight and Threshold (W/kg) are continuous lines, not a few dots.
  function withWeight(list: ChartDay[]): ChartDay[] {
    let weight: number | undefined;
    return list.map((day): ChartDay => {
      if (typeof day.weight === 'number' && day.weight > 0) weight = day.weight;
      return {
        ...day,
        weight,
        ftp_wkg: typeof day.ftp === 'number' && weight ? day.ftp / weight : undefined,
      };
    });
  }
  $: shownDays = mergeMatchedDays(days, matchedDays);
  $: allMarks = buildBreakthroughs(breakthroughs, from, drawTo, today, hideFuture, raceEvents);
  $: hasRaceEvents = allMarks.some((mark) => mark.kind);
  $: hasBreakthroughs = allMarks.some(isBreakthrough);
  // The two chips decide what the strip draws (see markersToShow). Booleans of their own, so toggling other chips doesn't redraw it.
  $: breakthroughsOn = isOn(group, 'breakthroughs', visibility);
  $: raceEventsOn = isOn(group, 'raceEvents', visibility);
  $: marks = markersToShow(allMarks, breakthroughsOn, raceEventsOn);
  $: notes = buildMarkers((markers ?? []).filter((marker) => marker.kinds.includes('note')), from, drawTo, today, hideFuture);

  // A group is a different set of axes as well as series, so it is drawn from scratch rather than
  // patched. The root has to be disposed each time or amCharts keeps every old chart alive.
  // A change of filter redraws the bars but keeps where the athlete had zoomed to.
  let drawnDays: ChartDay[] | null = null;
  let drawnGroup: HistoryGroup | null = null;
  $: if (mounted) {
    const sameHistory = days === drawnDays && group === drawnGroup;
    drawnDays = days;
    drawnGroup = group;
    rebuild(group, shownDays, marks, notes, sameHistory);
  }

  // ftp and ftp_wkg are mutually exclusive (one "Threshold" line, one unit at a time): 'w' | 'wkg' | 'off'.
  $: threshold = isOn(group, 'ftp', visibility) ? 'w' : isOn(group, 'ftp_wkg', visibility) ? 'wkg' : 'off';
  $: chips = [
    ...idsFor(group, filtered)
      .filter((id) => id !== 'ftp' && id !== 'ftp_wkg')
      .map((id) => ({
        id,
        label: SERIES[id].label,
        color: SERIES[id].color,
        on: isOn(group, id, visibility),
        title:
          scaleFactors[id] > 1
            ? `Drawn ${scaleFactors[id]}× larger to fit the load axis. Read its own scale from the numbers on the axis in its colour.`
            : '',
      })),
    // One chip, three states, cycled by toggleThreshold: Threshold Power (W) -> Threshold Power (W/kg) -> Off.
    ...(idsFor(group, filtered).includes('ftp')
      ? [
          {
            id: 'ftp',
            label: threshold === 'wkg' ? 'Threshold Power (W/kg)' : 'Threshold Power',
            color: SERIES.ftp.color,
            on: threshold !== 'off',
            title: 'Click to cycle: Threshold Power (W) → Threshold Power (W/kg) → Off',
          },
        ]
      : []),
    ...(hasBreakthroughs
      ? [{ id: 'breakthroughs', label: 'Breakthroughs', color: BREAKTHROUGH_COLOR, on: isOn(group, 'breakthroughs', visibility), title: '' }]
      : []),
    ...(hasRaceEvents
      ? [{ id: 'raceEvents', label: 'Race / Event', color: RACE_EVENT_COLORS.Race, color2: RACE_EVENT_COLORS.Event, on: isOn(group, 'raceEvents', visibility), title: 'Targets for activities tagged Race or Event' }]
      : []),
    ...(group === 'XPMC' && notes.length
      ? [{ id: 'notes', label: 'Notes', color: MARKER_KINDS.note.color, on: isOn(group, 'notes', visibility), title: '' }]
      : []),
  ];

  /** How much each rescaled series is enlarged by in the group being drawn (1 when it isn't rescaled). */
  let scaleFactors: Record<string, number> = {};

  // Enlargements that read easily off an axis; the largest that still keeps the series inside it.
  const NICE_FACTORS = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10, 15, 20, 25, 30, 40, 50, 100];
  const niceFactor = (ratio: number) => NICE_FACTORS.filter((factor) => factor <= ratio).pop() ?? 1;
  const plotField = (field: string) => `${field}_plot`;
  // Axis numbers: whole numbers from 10 up, one decimal below, so a label stays a few characters wide.
  const compact = (value: number) => (Math.abs(value) >= 10 ? String(Math.round(value)) : String(Number(value.toFixed(1))));

  // The scales in use, named in their own colours above the chart: left-hand scales over the left
  // edge and right-hand ones over the right. Short names, so a phone has room for all of them; the
  // load scale also names each enlarged series it carries.
  type AxisHeader = { id: string; side: 'left' | 'right'; parts: { text: string; color: string }[] };
  $: axisHeaders = ((): AxisHeader[] => {
    const headers: AxisHeader[] = [];
    for (const id of idsFor(group, filtered)) {
      const axisId = SERIES[id].axis as string;
      if (!isOn(group, id, visibility) || headers.some((header) => header.id === axisId)) continue;
      const parts = [{ text: AXES[axisId].short, color: AXES[axisId].color }];
      if (axisId === 'load') {
        for (const seriesId of Object.keys(scaleFactors)) {
          if (isOn(group, seriesId, visibility)) {
            parts.push({ text: SERIES[seriesId].label[0], color: SERIES[seriesId].labelColor ?? SERIES[seriesId].color });
          }
        }
      }
      headers.push({ id: axisId, side: AXES[axisId].side, parts });
    }
    return headers;
  })();

  // Height of the strip the breakthroughs sit in on the XPMC chart, and of the row of scale names.
  const STRIP_PX = 56;
  /** Height of the Planner notes' strip, above the breakthroughs'. */
  const NOTES_STRIP_PX = 28;
  const HEADER_PX = 30;
  /** Clear space between the names and the top of the axes' labels. */
  const HEADER_GAP_PX = 16;

  // Beyond this many days the form line is drawn without a dot on each day.
  const FORM_DOT_LIMIT = 500;

  const formatValue = (value: number, decimals: number) =>
    value.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function rebuild(
    g: HistoryGroup,
    chartDays: ChartDay[],
    chartMarks: ReturnType<typeof buildBreakthroughs>,
    chartNotes: ChartMarker[],
    keepZoom = false
  ) {
    // Redrawing for a toggle shouldn't lose where the athlete had zoomed to.
    const zoom = keepZoom && dateAxisRef ? [dateAxisRef.get('start'), dateAxisRef.get('end')] : null;
    root?.dispose();
    root = null;
    seriesById = {};
    axisById = {};
    if (!chartEl || !chartDays.length) return;

    // Tooltips may draw past the chart's own canvas: the marker/breakthrough strips sit at its very top edge, so a
    // tooltip there otherwise gets squeezed against (or cut off at) that edge instead of sitting above its marker.
    root = am5.Root.new(chartEl, { tooltipContainerBounds: { top: 240, right: 120, bottom: 120, left: 120 } });
    keepTooltipsOnScreen(root, chartEl);
    root.setThemes([am5themes_Animated.new(root)]);
    root.fontFamily = 'inherit';

    // On XPMC the breakthroughs get a strip of their own above the chart, where they can't sit on
    // top of the smaller XSS bars, and the Planner notes a thinner one above that. Turning either off
    // takes its strip away.
    const showStrip = g === 'XPMC' && chartMarks.length > 0;
    const showNotes = g === 'XPMC' && chartNotes.length > 0 && isOn(g, 'notes');
    const pct = (px: number) => Math.min(30, (px / (chartEl.clientHeight || 360)) * 100);
    const notesHeight = showNotes ? pct(NOTES_STRIP_PX) : 0;
    const breakthroughHeight = showStrip ? pct(STRIP_PX) : 0;
    const stripHeight = notesHeight + breakthroughHeight;
    const makeStrip = (height: number) =>
      root.container.children.push(
        am5xy.XYChart.new(root, {
          panX: false,
          panY: false,
          paddingTop: 0,
          paddingBottom: 0,
          paddingLeft: 0,
          paddingRight: 0,
          height: am5.percent(height),
        })
      );
    if (stripHeight) root.container.set('layout', root.verticalLayout);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const notesStrip: any = showNotes ? makeStrip(notesHeight) : null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const strip: any = showStrip ? makeStrip(breakthroughHeight) : null;
    const strips = [notesStrip, strip].filter(Boolean);

    // The strips only have their markers to go on, so every time axis is given the range of the
    // history explicitly: half a day either side, as a day's column is drawn around its midnight.
    const sharedRange = stripHeight
      ? { min: chartDays[0].t - 12 * 3600 * 1000, max: chartDays[chartDays.length - 1].t + 12 * 3600 * 1000, strictMinMax: true }
      : {};

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        layout: root.verticalLayout,
        // The time axis stays where it is: dragging selects a range rather than scrolling.
        panX: false,
        panY: false,
        paddingLeft: 0,
        paddingRight: 0,
        ...(stripHeight ? { height: am5.percent(100 - stripHeight), paddingTop: HEADER_PX } : {}),
      })
    );

    // One point per day: the state at the end of it, with what was done during it added up.
    const dateAxisRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 70 });
    dateAxisRenderer.grid.template.setAll({ strokeOpacity: 0.08 });
    dateAxisRenderer.labels.template.setAll({ fontSize: 11, fill: am5.color('#6B7280') });
    const dateAxis = chart.xAxes.push(
      am5xy.DateAxis.new(root, {
        baseInterval: { timeUnit: 'day', count: 1 },
        renderer: dateAxisRenderer,
        ...sharedRange,
      })
    );

    // The time axis never zooms (the date range is chosen in the sidebar or by dragging), so there is nothing to zoom back out of.
    chart.zoomOutButton.set('forceHidden', true);
    for (const each of strips) each.zoomOutButton.set('forceHidden', true);
    dateAxisRef = dateAxis;
    axisCentres = {};
    const measureAxes = () => {
      const next: Record<string, number> = {};
      for (const [axisId, axis] of Object.entries(axisById)) {
        if (!axis.get('visible')) continue;
        const bounds = axis.globalBounds();
        if (bounds && isFinite(bounds.left) && bounds.right > bounds.left) next[axisId] = Math.round((bounds.left + bounds.right) / 2);
      }
      const same = Object.keys(next).length === Object.keys(axisCentres).length && Object.entries(next).every(([axisId, x]) => axisCentres[axisId] === x);
      if (!same) axisCentres = next;
    };
    // On a phone the scales already take most of the width: smaller, tighter labels and abbreviated
    // big numbers.
    const narrow = chartEl.clientWidth < 640;
    let firstAxis = true;
    // The range of what each scale carries, worked out from all of it. Left to the chart it would go by
    // whatever is in view at the moment, and a line can end up running off the top.
    const extents: Record<string, { min: number; max: number }> = {};
    const medianTotals: Record<string, number> = {};
    // Scales whose range is fixed to line up with another's: no headroom, no rounding.
    const exactScales = new Set<string>();
    const axisRange = (id: string) => {
      const cfg = AXES[id];
      const extent = extents[id];
      if (!extent || extent.max <= extent.min) return { min: cfg.min, extraMax: cfg.extraMax };
      if (exactScales.has(id)) return { min: extent.min, max: extent.max, extraMin: 0, extraMax: 0, strictMinMax: true };
      if (cfg.medianHeadroom != null && medianTotals[id] > 0) {
        return { min: cfg.min ?? extent.min, max: Math.max(extent.max, medianTotals[id] * (1 + cfg.medianHeadroom)), extraMax: 0 };
      }
      return {
        min: cfg.min ?? extent.min,
        max: extent.max,
        extraMax: cfg.exactMax ? 0 : cfg.extraMax ?? 0.05,
        ...(cfg.exactMax ? { strictMinMax: true } : {}),
        ...(cfg.min == null ? { extraMin: 0.05 } : {}),
      };
    };
    const ensureAxis = (id: string) => {
      if (axisById[id]) return axisById[id];
      const cfg = AXES[id];
      const renderer = am5xy.AxisRendererY.new(root, { opposite: cfg.side === 'right', minGridDistance: narrow ? 30 : 40 });
      // Only the first scale draws gridlines; a second set would just be noise.
      renderer.grid.template.setAll({ strokeOpacity: firstAxis ? 0.08 : 0 });
      renderer.labels.template.setAll({
        fontSize: narrow ? 10 : 11,
        fill: am5.color(cfg.color),
        paddingLeft: narrow ? 3 : 4,
        paddingRight: narrow ? 3 : 4,
        marginLeft: 0,
        marginRight: 0,
      });
      if (id === 'load') {
        // Each tick reads as one number per series showing: the axis's own, then each enlarged
        // series' value in its colour, so they all share the one column of labels.
        renderer.labels.template.adapters.add('text', (text: string | undefined, target: any) => {
          const value = target.dataItem?.get('value');
          if (typeof value !== 'number') return text;
          const parts: string[] = [];
          if (isOn(g, 'ctl') || isOn(g, 'atl')) parts.push(`[${AXES.load.color}]${compact(value)}[/]`);
          for (const [seriesId, factor] of Object.entries(scaleFactors)) {
            if (isOn(g, seriesId)) parts.push(`[${SERIES[seriesId].labelColor ?? SERIES[seriesId].color}]${compact(value / factor)}[/]`);
          }
          return parts.join(' ');
        });
      }
      const axis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          renderer,
          strictMinMax: false,
          ...axisRange(id), // may override strictMinMax
          marginLeft: 0,
          marginRight: 0,
          ...(narrow ? { numberFormat: '#.#a' } : {}),
        })
      );

      firstAxis = false;
      axisById[id] = axis;
      return axis;
    };

    // Rescaled series are drawn at an enlargement of their own values, chosen so the biggest of them
    // reaches about as high as the biggest of the series the axis is mainly for.
    const peakOf = (field: string) => chartDays.reduce((max, day) => Math.max(max, (day[field] as number) || 0), 0);
    scaleFactors = {};
    for (const id of idsFor(g, filtered)) {
      if (!SERIES[id].rescale) continue;
      const anchor = Math.max(peakOf('ctl'), peakOf('atl'));
      const own = peakOf(SERIES[id].field);
      scaleFactors[id] = own > 0 && anchor > 0 ? niceFactor(anchor / own) : 1;
    }
    const plotDays = chartDays.map((day) => {
      const plotted: ChartDay = { ...day };
      // The form line takes each day's freshness colour, as the forecast chart's does.
      if (typeof day.tsbColor === 'string') {
        const colour = am5.color(day.tsbColor);
        (plotted as any).formStyle = { stroke: colour, fill: colour };
      }
      for (const [id, factor] of Object.entries(scaleFactors)) {
        const value = day[SERIES[id].field];
        if (typeof value === 'number') plotted[plotField(SERIES[id].field)] = value * factor;
      }
      return plotted;
    });

    // Work out each scale's range before drawing anything on it. Stacked bars add up; everything else is by itself.
    const grow = (axisId: string, low: number, high: number) => {
      const extent = extents[axisId] ?? { min: low, max: high };
      extent.min = Math.min(extent.min, low);
      extent.max = Math.max(extent.max, high);
      extents[axisId] = extent;
    };
    // On a shared scale only the visible series set the range: peers sharing an axis with no anchor (Low/High/Peak
    // Stress, say) can otherwise have one permanently dominate the scale even once it's toggled off, flattening the
    // others near zero. A series alone on its scale always sets it, so it is right when shown without a redraw.
    const counts = (id: string) => isOn(g, id) || !sharesAxis(g, id);
    for (const id of idsFor(g, filtered)) {
      const cfg = SERIES[id];
      if (cfg.stacked || !counts(id)) continue;
      const field = scaleFactors[id] ? plotField(cfg.field) : cfg.field;
      for (const day of plotDays) {
        const value = day[field];
        if (typeof value === 'number') grow(cfg.axis as string, cfg.type === 'column' ? Math.min(0, value) : value, value);
      }
    }
    for (const day of plotDays) {
      const stacked: Record<string, number> = {};
      for (const id of idsFor(g, filtered)) {
        const cfg = SERIES[id];
        const value = day[cfg.field];
        if (cfg.stacked && counts(id) && typeof value === 'number') stacked[cfg.axis as string] = (stacked[cfg.axis as string] ?? 0) + value;
      }
      for (const [axisId, total] of Object.entries(stacked)) grow(axisId, 0, total);
    }
    // Threshold power starts at p0 and rises k1 per unit of load, so it lines up with the load scale.
    if (extents.load && extents.power) {
      const fit = colinearScales(extents.load, extents.power, irParams?.ftp, chartDays, 'ftp', 'ctl');
      if (fit) {
        extents.load = fit.load;
        extents.power = fit.sig;
        exactScales.add('load');
        exactScales.add('power');
      }
    }
    for (const [axisId, cfg] of Object.entries(AXES)) {
      if (cfg.medianHeadroom == null) continue;
      const totals = plotDays
        .map((day) => idsFor(g, filtered).reduce((sum, id) => {
          const sc = SERIES[id];
          const value = day[sc.field];
          return sc.stacked && sc.axis === axisId && counts(id) && typeof value === 'number' ? sum + value : sum;
        }, 0))
        .filter((total) => total > 0)
        .sort((a, b) => a - b);
      if (totals.length) medianTotals[axisId] = (totals[Math.floor((totals.length - 1) / 2)] + totals[Math.floor(totals.length / 2)]) / 2;
    }

    for (const id of idsFor(g, filtered)) {
      const cfg = SERIES[id];
      const color = am5.color(cfg.color);
      const axis = ensureAxis(cfg.axis as string);
      const yField = scaleFactors[id] ? plotField(cfg.field) : cfg.field;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let series: any;
      if (cfg.type === 'column') {
        series = chart.series.push(
          am5xy.ColumnSeries.new(root, {
            name: cfg.label,
            xAxis: dateAxis,
            yAxis: axis,
            valueXField: 't',
            valueYField: yField,
            fill: color,
            stroke: color,
            stacked: !!cfg.stacked,
            clustered: false,
          })
        );
        series.columns.template.setAll({
          fillOpacity: cfg.stacked ? 0.85 : 0.5,
          strokeOpacity: 0,
          width: am5.percent(70),
          cornerRadiusTL: 1,
          cornerRadiusTR: 1,
        });
      } else {
        series = chart.series.push(
          am5xy.LineSeries.new(root, {
            name: cfg.label,
            xAxis: dateAxis,
            yAxis: axis,
            valueXField: 't',
            valueYField: yField,
            stroke: color,
            fill: color,
          })
        );
        series.strokes.template.setAll({
          strokeWidth: cfg.width ?? 2,
          strokeDasharray: cfg.dash ? [cfg.dash, cfg.dash] : undefined,
          strokeOpacity: 0.9,
        });
        if (id === 'tsb') {
          // Same treatment as the forecast chart: a soft line and a dot for each day, in the colour of
          // how fresh the athlete was. (A decade of daily dots would be a lot to draw, so long ranges keep the line.)
          series.strokes.template.setAll({ strokeWidth: 3, strokeOpacity: 0.6, templateField: 'formStyle' });
          if (chartDays.length <= FORM_DOT_LIMIT) {
            series.bullets.push(() =>
              am5.Bullet.new(root, {
                // The template supplies the fallback colour; each day's own colour comes from its data.
                sprite: am5.Circle.new(root, { radius: 3.5, strokeOpacity: 0, templateField: 'formStyle' }, am5.Template.new({ fill: color } as any) as any),
              })
            );
          }
        }
      }
      series.data.setAll(plotDays);
      seriesById[id] = series;
    }

    // Breakthrough markers and the tooltip ride on a hidden 0-1 axis of their own, so they sit in the
    // same place whichever scales the group is showing.
    const eventsAxis = addEventsAxis(root, chart);
    // Outside XPMC the markers stay along the bottom of the chart itself.
    if (g !== 'XPMC') seriesById.breakthroughs = addBreakthroughSeries(root, chart, dateAxis, eventsAxis, chartMarks);
    const dayTooltip = addDayTooltip(root, chart, dateAxis, eventsAxis, plotDays, (day) => {
      const lines: string[] = [];
      for (const id of idsFor(g, filtered)) {
        const cfg = SERIES[id];
        const value = day[cfg.field];
        if (!isOn(g, id) || typeof value !== 'number') continue;
        if (id === 'tsb') {
          // Form, the colour of the day's freshness, and how it splits across low, high and peak.
          const split = ['lf', 'hf', 'pf']
            .map((field, i) => (typeof day[field] === 'number' ? `[bold ${[SERIES.xlss.color, SERIES.xhss.labelColor ?? SERIES.xhss.color, SERIES.xpss.color][i]}]${formatValue(day[field] as number, 1)}[/]` : ''))
            .filter(Boolean)
            .join(' ');
          const dot = typeof day.tsbColor === 'string' ? day.tsbColor : cfg.color;
          lines.push(`[${dot}]●[/] ${cfg.label}: [bold]${formatValue(value, cfg.decimals)}[/] ${split}`);
          continue;
        }
        lines.push(
          `[${cfg.color}]●[/] ${cfg.label}: [bold]${formatValue(value / (cfg.scale ?? 1), cfg.decimals)}[/]${cfg.unit ? ' ' + cfg.unit : ''}`
        );
      }
      return lines;
    }, selected);
    addTodayGuide(dateAxis, chartDays, today);
    addProgramStartGuide(dateAxis, chartDays, programStart);

    if (strips.length) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const stripAxes: any[] = [];
      for (const each of strips) {
        const stripDateRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 70 });
        stripDateRenderer.grid.template.setAll({ strokeOpacity: 0.08 });
        stripDateRenderer.labels.template.set('forceHidden', true);
        const stripDateAxis = each.xAxes.push(
          am5xy.DateAxis.new(root, { baseInterval: { timeUnit: 'day', count: 1 }, renderer: stripDateRenderer, ...sharedRange })
        );
        stripAxes.push(stripDateAxis);
        if (each === strip) seriesById.breakthroughs = addBreakthroughDiamonds(root, strip, stripDateAxis, chartMarks);
        else addMarkerLanes(root, each, stripDateAxis, chartNotes, () => {}, ['note']);
        addTodayGuide(stripDateAxis, chartDays, today, false);
        addProgramStartGuide(stripDateAxis, chartDays, programStart, false);
        addSelectionCursor(root, each, stripDateAxis, selected);
        each.appear(keepZoom ? 0 : 300, keepZoom ? 0 : 50);
      }

      // Lined up with the chart below, whatever width its scales take: each strip's plot gets the
      // same left and right edges as the main plot.
      const align = () => {
        const bounds = chart.plotContainer.globalBounds();
        const left = Math.max(0, Math.round(bounds.left));
        const right = Math.max(0, Math.round(root.width() - bounds.right));
        for (const each of strips) {
          if (each.get('paddingLeft') !== left || each.get('paddingRight') !== right) each.setAll({ paddingLeft: left, paddingRight: right });
        }
      };
      chart.plotContainer.events.on('boundschanged', align);
      chart.events.on('datavalidated', align);
      // The scales settle over the first few frames, and the plot moves as they do.
      let settling = 12;
      root.events.on('frameended', () => {
        if (settling-- > 0) align();
      });

      // Zooming any one moves them all, and the cursor follows across.
      const allAxes = [dateAxis, ...stripAxes];
      let syncing = false;
      for (const key of ['start', 'end'] as const) {
        for (const axis of allAxes) {
          axis.on(key, (value: number) => {
            if (syncing) return;
            syncing = true;
            for (const other of allAxes) if (other !== axis) other.set(key, value);
            syncing = false;
          });
        }
      }
      const cursors = [chart, ...strips].map((each) => each.get('cursor'));
      for (const cursor of cursors) cursor.set('syncWith', cursors.filter((other) => other !== cursor));
      // The day's values show only while the pointer is over the chart itself, so they don't cover
      // a marker's own tooltip while it is being read in a strip.
      const dayBox = dayTooltip.get('tooltip');
      dayBox.set('forceHidden', true);
      chart.plotContainer.events.on('pointerover', () => dayBox.set('forceHidden', false));
      chart.plotContainer.events.on('pointerout', () => dayBox.set('forceHidden', true));
    }

    // Anything not showing yet starts hidden, and only the scales of what is showing are drawn.
    for (const id of idsFor(g, filtered)) {
      if (!isOn(g, id)) seriesById[id]?.hide(0);
    }
    syncAxes(g);
    // The scales settle into place over the first few frames, and move again if the chart is resized.
    let measuring = 20;
    root.events.on('frameended', () => {
      if (measuring-- > 0) measureAxes();
    });
    chart.plotContainer.events.on('boundschanged', measureAxes);
    chart.appear(keepZoom ? 0 : 300, keepZoom ? 0 : 50);
    if (zoom) dateAxis.zoom(zoom[0], zoom[1], 0);
  }

  /** A scale nothing on it is showing has no reason to take up room. */
  function syncAxes(g: HistoryGroup) {
    for (const [axisId, axis] of Object.entries(axisById)) {
      const inUse = idsFor(g, filtered).some((id) => SERIES[id].axis === axisId && isOn(g, id));
      axis.set('visible', inUse);
    }
  }

  function toggle(id: string) {
    const next = !isOn(group, id);
    visibility = { ...visibility, [key(group, id)]: next };
    saveStorage('progressionCharts', 'lines', visibility);
    // The strip is redrawn with what the two chips leave in it, which the change of `marks` does.
    if (id === 'raceEvents' || id === 'breakthroughs') return;
    // The load axis lists the scale of every series showing on it in its labels, and there is no
    // way to make an axis re-read them short of drawing it again. The same goes for the strips the
    // breakthroughs and the notes have above the XPMC chart, which come and go. A scale shared by several
    // series (XSS split, loads, stress) has its min/max set from whichever of them are on at rebuild time, so
    // it needs redoing on toggle too, or a much bigger series left off can leave it stuck too large (or too small).
    if (id === 'notes' || sharesAxis(group, id)) {
      rebuild(group, shownDays, marks, notes, true);
      return;
    }
    const series = seriesById[id];
    if (next) series?.show();
    else series?.hide();
    syncAxes(group);
  }

  // ftp and ftp_wkg sit on their own axes (power / power_wkg) with no peer competing for the scale, so unlike
  // toggle() above this never needs a full rebuild: showing/hiding each series and syncing axis visibility is enough.
  function toggleThreshold() {
    const cur = isOn(group, 'ftp') ? 'w' : isOn(group, 'ftp_wkg') ? 'wkg' : 'off';
    const next = cur === 'w' ? 'wkg' : cur === 'wkg' ? 'off' : 'w';
    visibility = { ...visibility, [key(group, 'ftp')]: next === 'w', [key(group, 'ftp_wkg')]: next === 'wkg' };
    saveStorage('progressionCharts', 'lines', visibility);
    if (next === 'w') seriesById.ftp?.show();
    else seriesById.ftp?.hide();
    if (next === 'wkg') seriesById.ftp_wkg?.show();
    else seriesById.ftp_wkg?.hide();
    syncAxes(group);
  }

  onMount(() => {
    mounted = true;
  });
  onDestroy(() => {
    root?.dispose();
    root = null;
  });
</script>

<div class="flex flex-col gap-2">
  <div class="relative w-full h-[300px] md:h-[360px]">
    <!-- The scales, named in their own colours. Drawn before (so under) the chart, whose tooltips have to cover them. They sit just above the axes: under the breakthrough
         strip when there is one, otherwise at the top of the chart. -->
    <div
      class="absolute left-0 right-0 pointer-events-none text-[10px] md:text-xs font-semibold leading-none"
      bind:clientWidth={headerRowWidth}
      style="top: {stripsPx}px; height: {HEADER_PX - HEADER_GAP_PX}px"
      aria-hidden="true"
    >
      {#each axisHeaders as header (header.id)}
        {#if axisCentres[header.id] != null}
          <span class="absolute bottom-0 inline-flex gap-1 whitespace-nowrap" bind:clientWidth={headerWidths[header.id]} style="left: {headerLeft[header.id] ?? axisCentres[header.id]}px">
            {#each header.parts as part}<span style="color: {part.color}">{part.text}</span>{/each}
          </span>
        {/if}
      {/each}
    </div>
    <div
      bind:this={chartEl}
      role="img"
      aria-label="{group} progression chart. Drag across it to choose the dates; the date range can also be set in the filters."
      class="absolute left-0 right-0 bottom-0" style="top: {stripsPx ? 0 : HEADER_PX}px"></div>
    {#if !days.length}
      <div class="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">
        No fitness history in this date range.
      </div>
    {/if}
  </div>

  <div class="flex flex-row flex-wrap gap-2" role="group" aria-label="Chart series">
    {#each chips as chip (chip.id)}
      <button
        type="button"
        class={cn(
          'inline-flex items-center gap-1.5 rounded-full border border-solid px-2.5 py-1 text-xs cursor-pointer bg-white transition-opacity',
          chip.on ? 'border-border text-foreground' : 'border-border text-muted-foreground opacity-60'
        )}
        aria-pressed={chip.on}
        title={chip.title}
        on:click={() => (chip.id === 'ftp' ? toggleThreshold() : toggle(chip.id))}
      >
        <span class="inline-block h-2.5 w-2.5 rounded-full" style="background: {chip.on ? ('color2' in chip ? `linear-gradient(135deg, ${chip.color} 50%, ${chip.color2} 50%)` : chip.color) : 'transparent'}; border: 1.5px solid {chip.color}"></span>
        {chip.label}
      </button>
    {/each}
  </div>
</div>

<style>
  :global(.am5-layer) {
    font-family: inherit;
  }
</style>
